class PredictiveSearch extends HTMLElement {
  /**
   * Initialise le composant de recherche prédictive
   * @constructor
   */
  constructor() {
    super();

    // Stockage des références aux éléments DOM pour éviter des appels répétés à querySelector
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.resetButton = this.querySelector('.search__button-reset');

    // Initialisation de l'état
    this.close();
    this.bindEvents();
  }

  /**
   * Attache tous les écouteurs d'événements nécessaires au fonctionnement du composant
   * @returns {void}
   */
  bindEvents() {
    // Écouteur pour l'input de recherche avec debounce
    if (this.input) {
      this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
    }

    // Écouteur pour le bouton de réinitialisation
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.handleResetClick.bind(this));
    }

    // Écouteur global pour les clics en dehors de la zone de recherche
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  /**
   * Gère le clic sur le bouton de réinitialisation
   * @param {Event} event - L'événement de clic
   * @returns {void}
   */
  handleResetClick(event) {
    event.preventDefault();
    this.closeSearch();
  }

  /**
   * Gère les clics sur le document pour fermer la recherche si nécessaire
   * @param {Event} event - L'événement de clic
   * @returns {void}
   */
  handleDocumentClick(event) {
    // Ferme la recherche si on clique en dehors de la zone de recherche
    const isSearchOpen = this.classList.contains('is-open');
    const isNotSearchIcon = !event.target.classList.contains('mm-icon-search');
    const isClickOutside = !this.contains(event.target);
    const isNotOverlay = !event.target.classList.contains('search-overlay');

    if (isSearchOpen && isNotSearchIcon && isClickOutside && isNotOverlay) {
      this.closeSearch();
    }
  }

  /**
   * Gère les changements dans l'input de recherche
   * @returns {void}
   */
  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  /**
   * Récupère les résultats de recherche prédictive via une requête fetch
   * @param {string} searchTerm - Le terme de recherche saisi par l'utilisateur
   * @returns {void}
   */
  getSearchResults(searchTerm) {
    const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search-v2`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(`Erreur ${response.status}`);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search-v2')?.innerHTML;

        if (resultsMarkup) {
          this.predictiveSearchResults.innerHTML = resultsMarkup;
          this.open();
        }
      })
      .catch((error) => {
        console.error('Erreur de recherche prédictive:', error);
        this.close();
      });
  }

  /**
   * Affiche les résultats de recherche
   * @returns {void}
   */
  open() {
    this.predictiveSearchResults.style.display = 'block';
  }

  /**
   * Masque les résultats de recherche
   * @returns {void}
   */
  close() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
    }
  }

  /**
   * Ferme complètement la recherche et réinitialise l'input
   * @returns {void}
   */
  closeSearch() {
    this.classList.remove('is-open');
    if (this.input) {
      this.input.value = '';
    }
    this.close();

    document.body.classList.remove('search-is-open');


    // Gérer la classe mm-bg-white du header si nécessaire
    const header = document.querySelector('.mm-header');
    if (header && header.classList.contains('mm-header-index')) {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      if (scrollPosition <= 100) {
        header.classList.remove('mm-bg-white');
      }
    }
  }

  /**
   * Fonction utilitaire de debounce pour limiter les appels fréquents
   * @param {Function} fn - La fonction à exécuter après le délai
   * @param {number} wait - Le délai en millisecondes
   * @returns {Function} - La fonction avec debounce appliqué
   */
  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }
}

/**
 * Définit l'élément personnalisé 'predictive-search'
 */
customElements.define('predictive-search', PredictiveSearch);
